import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { State, PartialAction, Position } from 'src/types'
import { useDispatcher } from 'src/features/atoms/glove/japan/logic/dispatchGenerator'
import { useOriginDispatcher } from 'src/hooks/useOriginDispatcher'
import { useLocale } from 'src/locales/useLocale'
import {
  colorCautionGenerator,
  hamidashiObjectSwitcher,
  useColorObjects,
  colorSelectObjects as colorSelectObjectsGenerator,
  useSelectPartsObject
} from 'src/features/atoms/glove/japan/logic/colorSetting'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'
import { DISPATCHER } from 'src/features/atoms/glove/japan/constants/actionType'
import {
  PARTS_BUTTON_OPTION,
  LEATHER_COLOR_BUTTON_OPTION,
  LACE_COLOR_BUTTON_OPTION,
  EDGE_COLOR_BUTTON_OPTION,
  STITCH_COLOR_BUTTON_OPTION,
  HAMIDASHI_COLOR_BUTTON_OPTION,
  MOUTON_BUTTON_OPTION,
  ATOMS_LABEL_BUTTON_OPTION
} from '../constants/colors'
import { positionChecker, backStyleChecker } from 'src/logic'
import { APL_UR002_PLUS_PRODUCT_NUMBER } from '../constants/baseModel'

type Props = {
  state: State
  selectedIndex: number
  figurePanelNum: number
  position: Position
  isStaffOnly: boolean
  dispatch: React.Dispatch<PartialAction>
}

export const ColorSetter: React.FC<Props> = ({ state, selectedIndex, figurePanelNum, position, isStaffOnly, dispatch }) => {
  const { tanner, parts, atomsLabel, lace, edge, stitch, hamidashi, mouton } = state
  const partsKey = parts?.value
  const { isFirst, isCatcher } = positionChecker(position)
  const { isFirstBackStyle } = backStyleChecker(state.backStyle?.value)
  const isRegularBackStyle = state.backStyle.value === 'regularBackStyle'
  const isAPL_UR002_PLUS = state.baseModel.productNumber === APL_UR002_PLUS_PRODUCT_NUMBER
  const isBeforeSelectBackStyleForCatcher = isCatcher && !isAPL_UR002_PLUS && state.backStyle.value === 'unselected'
  const { t } = useLocale()
  const colorSelectObjects = colorSelectObjectsGenerator(tanner?.value, partsKey, isStaffOnly, t)
  const partsObjects = useSelectPartsObject(figurePanelNum, position, state, t)
  const handle = {
    [partsKey]: useOriginDispatcher(DISPATCHER, partsKey, dispatch, colorSelectObjects),
    parts: useOriginDispatcher(DISPATCHER, 'parts', dispatch, partsObjects),
    lace: useDispatcher('lace', dispatch, t),
    edge: useDispatcher('edge', dispatch, t),
    stitch: useDispatcher('stitch', dispatch, t),
    hamidashi: useDispatcher('hamidashi', dispatch, t),
    mouton: useDispatcher('mouton', dispatch, t),
    atomsLabel: useDispatcher('atomsLabel', dispatch, t)
  }
  const colorObjects = useColorObjects(position, state)
  if (!colorObjects || tanner?.value === 'unselected') return <></>
  const partsLabel = parts?.label
  return (
    <TabPanel {...{ selectedIndex }} index={1}>
      <SelectCard summary={t.PARTS} selectedLabel={partsLabel} objects={partsObjects} handleChange={handle.parts} className={PARTS_BUTTON_OPTION} />
      <SelectCard
        summary={`${partsLabel}${t.COLOR}`}
        selectedLabel={colorObjects[partsKey]?.label}
        selectedColor={colorObjects[partsKey]?.color}
        objects={colorSelectObjects}
        isError={colorObjects[partsKey]?.value === 'unselected'}
        handleChange={handle[partsKey]}
        className={LEATHER_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.EDGE}
        selectedLabel={edge.label}
        selectedColor={edge.color}
        objects={t.EDGE_COLOR_OBJECTS}
        isError={edge.value === 'unselected'}
        defaultExpanded={false}
        handleChange={handle.edge}
        className={EDGE_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.HAMIDASHI}
        selectedLabel={hamidashi.label}
        selectedColor={hamidashi.color}
        objects={hamidashiObjectSwitcher(position, t)}
        isError={hamidashi.value === 'unselected'}
        defaultExpanded={false}
        handleChange={handle.hamidashi}
        isDisplay={!isFirst}
        className={HAMIDASHI_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.LACE}
        selectedLabel={lace.label}
        selectedColor={lace.color}
        objects={t.LACE_COLOR_OBJECTS}
        isError={lace.value === 'unselected'}
        defaultExpanded={false}
        caution={colorCautionGenerator('lace', t)}
        handleChange={handle.lace}
        className={LACE_COLOR_BUTTON_OPTION}
      />
      <SelectCard
        summary={t.STITCH}
        selectedLabel={stitch.label}
        selectedColor={stitch.color}
        objects={t.STITCH_COLOR_OBJECTS}
        isError={stitch.value === 'unselected'}
        defaultExpanded={false}
        handleChange={handle.stitch}
        className={STITCH_COLOR_BUTTON_OPTION}
      />
      <SelectCardWithImage
        summary={t.MOUTON}
        selectedLabel={mouton.label}
        selectedColor={mouton.color}
        objects={t.MOUTON_COLOR_OBJECTS}
        isError={mouton.value === 'unselected'}
        handleChange={handle.mouton}
        isDisplay={!isFirstBackStyle && !isRegularBackStyle}
        imageHeight={100}
        className={MOUTON_BUTTON_OPTION}
        isShowLabel
      />
      <SelectCardWithImage
        summary={t.LABEL}
        selectedLabel={atomsLabel.label}
        selectedColor={atomsLabel.color}
        objects={t.LABEL_COLOR_OBJECTS}
        isError={atomsLabel.value === 'unselected'}
        imageWidth={120}
        imageHeight={120}
        handleChange={handle.atomsLabel}
        disabled={isBeforeSelectBackStyleForCatcher}
        description={isBeforeSelectBackStyleForCatcher ? t.SELECT_BACK_STYLE_DESCRIBE : ''}
        className={ATOMS_LABEL_BUTTON_OPTION}
      />
    </TabPanel>
  )
}
